import { simpleApiService } from './simpleApiService'
import { useAuthStore } from '../store/authStore'
import { 
  Paciente, 
  PacienteForm, 
  DatosFacturacion, 
  SaldoPaciente,
  PacienteFilters,
  PacienteConSaldo,
  ApiResponse, 
  PaginatedResponse 
} from '../types'

class PacienteService {
  private getToken(): string | undefined {
    const token = useAuthStore.getState().token
    return token || undefined
  }

  // Obtener pacientes con paginación y filtros
  async getPacientes(
    page: number = 1, 
    perPage: number = 10, 
    filters?: PacienteFilters
  ): Promise<PaginatedResponse<Paciente>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: perPage.toString(),
    })

    if (filters?.search) {
      params.append('search', filters.search)
    }
    if (filters?.tipo_documento) {
      params.append('tipo_documento', filters.tipo_documento)
    }
    if (filters?.sector) {
      params.append('sector', filters.sector)
    }
    if (filters?.saldo_estado) {
      params.append('saldo_estado', filters.saldo_estado)
    }
    if (filters?.sort_by) {
      params.append('sort_by', filters.sort_by)
    }
    if (filters?.sort_order) {
      params.append('sort_order', filters.sort_order)
    }

        const response = await simpleApiService.get<PaginatedResponse<Paciente>>(
          `/pacientes/?${params.toString()}`,
          this.getToken()
        )
    return response
  }

  // Obtener un paciente por ID
  async getPaciente(id: number): Promise<Paciente> {
    const response = await simpleApiService.get<Paciente>(`/pacientes/${id}`, this.getToken())
    return response
  }

  // Crear nuevo paciente
  async createPaciente(data: PacienteForm): Promise<Paciente> {
    const response = await simpleApiService.post<Paciente>('/pacientes', data, this.getToken())
    return response
  }

  // Actualizar paciente
  async updatePaciente(id: number, data: Partial<PacienteForm>): Promise<Paciente> {
    const response = await simpleApiService.put<Paciente>(`/pacientes/${id}`, data, this.getToken())
    return response
  }

  // Eliminar paciente
  async deletePaciente(id: number): Promise<void> {
    await simpleApiService.delete(`/pacientes/${id}`, this.getToken())
  }

  // Buscar pacientes por documento
  async searchPacienteByDocument(tipoDocumento: string, numeroDocumento: string): Promise<Paciente | null> {
    try {
      const response = await simpleApiService.get<Paciente>(
        `/pacientes/search?tipo_documento=${tipoDocumento}&numero_documento=${numeroDocumento}`,
        this.getToken()
      )
      return response
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  }

  // Datos de facturación
  async getDatosFacturacion(pacienteId: number): Promise<DatosFacturacion[]> {
    const response = await simpleApiService.get<DatosFacturacion[]>(
      `/pacientes/${pacienteId}/datos-facturacion`,
      this.getToken()
    )
    return response
  }

  async createDatosFacturacion(pacienteId: number, data: Omit<DatosFacturacion, 'id' | 'paciente_id' | 'created_at' | 'updated_at'>): Promise<DatosFacturacion> {
    const response = await simpleApiService.post<DatosFacturacion>(
      `/pacientes/${pacienteId}/datos-facturacion`, 
      data,
      this.getToken()
    )
    return response
  }

  async updateDatosFacturacion(pacienteId: number, datosId: number, data: Partial<DatosFacturacion>): Promise<DatosFacturacion> {
    const response = await simpleApiService.put<DatosFacturacion>(
      `/pacientes/datos-facturacion/${datosId}`, 
      data,
      this.getToken()
    )
    return response
  }

  async deleteDatosFacturacion(pacienteId: number, datosId: number): Promise<void> {
    await simpleApiService.delete(`/pacientes/datos-facturacion/${datosId}`, this.getToken())
  }

  // Saldos del paciente
  async getSaldosPaciente(pacienteId: number): Promise<SaldoPaciente[]> {
    const response = await simpleApiService.get<SaldoPaciente[]>(
      `/pacientes/${pacienteId}/saldos`,
      this.getToken()
    )
    return response
  }

  async createSaldoPaciente(pacienteId: number, data: Omit<SaldoPaciente, 'id' | 'paciente_id' | 'fecha_registro'>): Promise<SaldoPaciente> {
    // Convertir el monto a string para que la API lo procese como Decimal
    const apiData = {
      ...data,
      monto: data.monto.toString()
    }
    
    const response = await simpleApiService.post<SaldoPaciente>(
      `/pacientes/${pacienteId}/saldos`, 
      apiData,
      this.getToken()
    )
    return response
  }

  async updateSaldoPaciente(pacienteId: number, saldoId: number, data: Partial<SaldoPaciente>): Promise<SaldoPaciente> {
    // Convertir el monto a string si está presente
    const apiData = {
      ...data,
      ...(data.monto && { monto: data.monto.toString() })
    }
    
    const response = await simpleApiService.put<SaldoPaciente>(
      `/pacientes/${pacienteId}/saldos/${saldoId}`, 
      apiData,
      this.getToken()
    )
    return response
  }

  async deleteSaldoPaciente(pacienteId: number, saldoId: number): Promise<void> {
    await simpleApiService.delete(`/pacientes/${pacienteId}/saldos/${saldoId}`, this.getToken())
  }

  // Estadísticas del paciente
  async getPacienteStats(pacienteId: number): Promise<{
    total_atenciones: number
    total_gastado: number
    saldo_actual: number
    ultima_atencion: string | null
  }> {
    const response = await simpleApiService.get<any>(`/pacientes/${pacienteId}/stats`, this.getToken())
    return response
  }

  // Obtener el número total de pacientes
  async getTotalPacientes(): Promise<{ total: number }> {
    const response = await simpleApiService.get<{ total: number }>('/pacientes/count', this.getToken())
    return response
  }

  // Obtener el número de pacientes por rango de fechas
  async getPacientesCountByDateRange(fechaDesde: string, fechaHasta: string): Promise<{ 
    total: number, 
    fecha_desde: string, 
    fecha_hasta: string 
  }> {
    const params = new URLSearchParams({
      fecha_desde: fechaDesde,
      fecha_hasta: fechaHasta
    })
    
    const response = await simpleApiService.get<{ 
      total: number, 
      fecha_desde: string, 
      fecha_hasta: string 
    }>(`/pacientes/count/date-range?${params.toString()}`, this.getToken())
    return response
  }

  // Obtener pacientes con saldo negativo
  async getPacientesConSaldoNegativo(limit: number = 5): Promise<PacienteConSaldo[]> {
    const response = await simpleApiService.get<{ pacientes: PacienteConSaldo[], total: number }>(
      `/pacientes/saldo-negativo?limit=${limit}`,
      this.getToken()
    )
    return response.pacientes
  }
}

export const pacienteService = new PacienteService()
